import PropTypes from "prop-types";

const statusClasses = {
    NORMAL_ACTIVE: "bg-gray-800 text-gray-200",
    LONG_HOLD_REVIEW: "bg-amber-900/50 text-amber-200",
    APPROVED_EXIT: "bg-emerald-900/40 text-emerald-200",
    EXITED: "bg-gray-800 text-gray-400",
};

const alertBadges = {
    aging: "bg-amber-900/60 text-amber-200",
    awaiting_exit: "bg-rose-900/60 text-rose-200",
};

function formatDays(days) {
    if (days === null || days === undefined) return "—";
    if (days < 1) return `${days.toFixed(2)}d`;
    return `${days.toFixed(1)}d`;
}

function formatDuration(seconds) {
    if (seconds === null || seconds === undefined) return "—";
    const days = seconds / 86400;
    if (days >= 1) return `${days.toFixed(1)}d`;
    const hours = seconds / 3600;
    if (hours >= 1) return `${hours.toFixed(1)}h`;
    const mins = seconds / 60;
    return `${mins.toFixed(0)}m`;
}

export default function HorizonLongHoldPanel({
    positions,
    onToggle,
    actionMessage,
    pendingPositionIds = new Set(),
    className = "",
}) {
    const openPositions = Array.isArray(positions)
        ? positions.filter((p) => p.status !== "EXITED")
        : [];

    return (
        <div className={`bg-gray-900 rounded-xl p-5 sm:p-6 shadow-md h-full ${className}`}>
            <div className="flex items-center justify-between gap-3 mb-3">
                <h3 className="text-lg font-semibold">Horizon Long-Hold Control</h3>
                <span className="text-xs text-gray-400">Manual exit approvals</span>
            </div>
            {actionMessage && (
                <div
                    className={`text-xs mb-3 ${/^failed/i.test(actionMessage) ? "text-red-300" : "text-emerald-200"}`}
                    data-testid="horizon-action-msg"
                >
                    {actionMessage}
                </div>
            )}
            {openPositions.length === 0 ? (
                <div className="text-sm text-gray-400" data-testid="horizon-long-hold-empty">
                    No Horizon long-hold positions at the moment.
                </div>
            ) : (
                <div className="space-y-3">
                    {openPositions.map((pos) => {
                        const badge = statusClasses[pos.status] || statusClasses.NORMAL_ACTIVE;
                        const hasConfirmedPermission = typeof pos.exit_permission === "boolean";
                        const allowExit = hasConfirmedPermission && pos.exit_permission === true;
                        const positionId = pos.position_id || "";
                        const isPending = pendingPositionIds instanceof Set && positionId ? pendingPositionIds.has(positionId) : false;
                        const alertLevel = pos.long_hold_alert_level || "none";
                        const alertBadge = alertBadges[alertLevel];
                        return (
                            <div
                                key={pos.position_id || pos.coin}
                                className="border border-gray-800 rounded-lg p-3 bg-gray-950/40"
                            >
                                <div className="flex items-start justify-between gap-3">
                                    <div>
                                        <div className="text-sm font-semibold">{pos.coin}</div>
                                        <div className={`inline-flex items-center px-2 py-0.5 rounded text-[0.7rem] ${badge}`}>
                                            {!hasConfirmedPermission
                                                ? "Review Unavailable"
                                                : pos.status === "LONG_HOLD_REVIEW"
                                                ? "Long Hold Review"
                                                : pos.status === "APPROVED_EXIT" && allowExit
                                                ? "Approval Granted"
                                                : "Active"}
                                        </div>
                                        {alertBadge && (
                                            <div
                                                className={`mt-1 inline-flex items-center px-2 py-0.5 rounded text-[0.7rem] ${alertBadge}`}
                                                data-testid={`horizon-alert-${alertLevel}`}
                                            >
                                                {alertLevel === "aging" && "Aging – review soon"}
                                                {alertLevel === "awaiting_exit" && "Awaiting exit after approval"}
                                            </div>
                                        )}
                                    </div>
                                    <div className="flex flex-col items-end gap-1">
                                        <button
                                            type="button"
                                            onClick={() => onToggle(positionId, !allowExit)}
                                            className="text-xs px-3 py-1 rounded bg-cyan-900/50 border border-cyan-700 hover:bg-cyan-800 disabled:cursor-not-allowed disabled:opacity-60"
                                            aria-label={allowExit ? "Revoke exit approval" : "Approve exit"}
                                            disabled={isPending || !positionId || !hasConfirmedPermission}
                                        >
                                            {isPending ? "Pending…" : allowExit ? "Revoke Exit" : "Approve Exit"}
                                        </button>
                                        {isPending && (
                                            <span className="text-[0.7rem] text-cyan-200" data-testid={`horizon-pending-${positionId}`}>
                                                Approval request pending backend confirmation
                                            </span>
                                        )}
                                        {pos.exit_permission_required && !allowExit && (
                                            <span className="text-[0.7rem] text-amber-200">Exit permission required</span>
                                        )}
                                    </div>
                                </div>
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-2 text-xs text-gray-300">
                                    <div>
                                        <div className="text-gray-500">Entry</div>
                                        <div>{pos.entry_price ? `$${pos.entry_price.toFixed(2)}` : "—"}</div>
                                    </div>
                                    <div>
                                        <div className="text-gray-500">Current</div>
                                        <div>
                                            {pos.current_price ? `$${pos.current_price.toFixed(2)}` : "—"}
                                            {pos.unrealized_pnl_pct !== null && pos.unrealized_pnl_pct !== undefined && (
                                                <span className={
                                                    pos.unrealized_pnl_pct >= 0
                                                        ? "text-green-300 ml-1"
                                                        : "text-red-300 ml-1"
                                                }>
                                                    {pos.unrealized_pnl_pct.toFixed(2)}%
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                    <div>
                                        <div className="text-gray-500">Position Size</div>
                                        <div>{pos.size ? pos.size : "—"}</div>
                                    </div>
                                    <div>
                                        <div className="text-gray-500">Long Hold Age</div>
                                        <div>{formatDuration(pos.time_in_position_seconds) || formatDays(pos.time_in_position_days)}</div>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

HorizonLongHoldPanel.propTypes = {
    positions: PropTypes.arrayOf(PropTypes.object),
    onToggle: PropTypes.func.isRequired,
    actionMessage: PropTypes.string,
    pendingPositionIds: PropTypes.instanceOf(Set),
    className: PropTypes.string,
};

